import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Tooltip,
  Avatar,
  Tab,
  Tabs,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Badge,
  LinearProgress,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel as CheckboxLabel
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Email,
  Phone,
  School,
  AttachMoney,
  Schedule,
  LocationOn,
  ExpandMore,
  FilterList,
  Download,
  Person,
  Work,
  Language,
  CloudDownload,
  Send,
  Refresh,
  PictureAsPdf,
  DeleteOutline
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet, apiPut, apiDelete } from '../../services/api';
import { PaymentType, JobCategory } from '../../types/job';

// Types
interface Applicant {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  avatar?: string;
}

interface Job {
  _id: string;
  title: string;
  company: string;
  location: string;
  isTeachingJob: boolean;
  subjectsToTeach?: string[];
  paymentType?: string;
  paymentRate?: {
    min: number;
    max: number;
    currency: string;
  };
}

interface TeachingApplication {
  _id: string;
  applicant?: Applicant;
  job?: Job;
  status: 'applied' | 'under_review' | 'shortlisted' | 'interview_scheduled' | 'interviewed' | 'offered' | 'hired' | 'rejected' | 'withdrawn';
  preferredSubjects: string[];
  preferredPaymentType?: string;
  expectedRate?: {
    amount: number;
    currency: string;
  };
  preferredTeachingMode?: string;
  highestQualification?: string;
  teachingExperience?: string;
  availability?: string;
  languagesSpoken?: string[];
  teachingCredentials?: string[];
  cvFile?: string;
  cvFileOriginalName?: string;
  resume?: string;
  resumeOriginalName?: string;
  additionalDocuments?: Array<{
    url: string;
    originalName: string;
  }>;
  coverLetter?: string;
  appliedAt: string;
  approvalEmailSent?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`recruitment-tabpanel-${index}`}
      aria-labelledby={`recruitment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const RecruitmentManagement: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [applications, setApplications] = useState<TeachingApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<TeachingApplication | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | null;
    application: TeachingApplication | null;
  }>({ open: false, action: null, application: null });
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
  }, [page, rowsPerPage, statusFilter]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter) {
        params.status = statusFilter;
      }

      const data = await apiGet<any>(`/job-applications/admin/teaching`, params);

      // Check for API error
      if (data.success === false) {
        throw new Error(data.error || 'Failed to load applications');
      }

      setApplications(data.data || []);
      setTotal(data.pagination?.total || data.total || 0);
    } catch (err: any) {
      setError('Failed to load applications: ' + (err.message || 'Unknown error'));
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    const search = searchTerm.toLowerCase();
    return (
      app.applicant?.firstName?.toLowerCase().includes(search) ||
      app.applicant?.lastName?.toLowerCase().includes(search) ||
      app.applicant?.email?.toLowerCase().includes(search) ||
      app.job?.title?.toLowerCase().includes(search) ||
      app.preferredSubjects?.some(s => s.toLowerCase().includes(search))
    );
  });

  const handleViewDetails = (application: TeachingApplication) => {
    setSelectedApplication(application);
    setDetailsDialog(true);
    setCurrentTab(0);
  };

  const handleAction = (application: TeachingApplication, action: 'approve' | 'reject') => {
    setActionDialog({ open: true, action, application });
    setActionNotes('');
  };

  const handleActionConfirm = async () => {
    if (!actionDialog.application || !actionDialog.action) return;

    setActionLoading(true);
    try {
      const endpoint = actionDialog.action === 'approve'
        ? `/job-applications/${actionDialog.application._id}/approve`
        : `/job-applications/${actionDialog.application._id}/reject`;

      const response = await apiPut<any>(
        endpoint,
        {
          adminFeedback: actionNotes,
          rejectionReason: actionDialog.action === 'reject' ? actionNotes : undefined
        }
      );

      // Check for API error
      if (response.success === false) {
        throw new Error(response.error || 'Failed to update application');
      }

      setSuccess(`Application ${actionDialog.action === 'approve' ? 'approved' : 'rejected'} successfully`);
      loadApplications();
      setActionDialog({ open: false, action: null, application: null });
    } catch (err: any) {
      setError('Failed to update application: ' + (err.message || 'Unknown error'));
      console.error('Error updating application:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'info';
      case 'under_review': return 'warning';
      case 'shortlisted': return 'primary';
      case 'interview_scheduled': return 'info';
      case 'interviewed': return 'default';
      case 'offered': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'error';
      case 'withdrawn': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const applicationsToExport = filteredApplications;

    // Add title
    doc.setFontSize(18);
    doc.text('Teaching Job Applications Report', 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
    doc.text(`Total Applications: ${applicationsToExport.length}`, 14, 38);

    // Prepare table data
    const tableData = applicationsToExport.map((app) => [
      `${app.applicant?.firstName || ''} ${app.applicant?.lastName || ''}`,
      app.applicant?.email || 'N/A',
      app.applicant?.phone || 'N/A',
      app.job?.title || 'N/A',
      app.job?.company || 'N/A',
      app.preferredSubjects?.join(', ') || 'N/A',
      app.preferredTeachingMode?.replace('_', ' ') || 'N/A',
      app.status.replace('_', ' '),
      formatDate(app.appliedAt),
      app.highestQualification || 'N/A',
      app.teachingExperience || 'N/A',
      app.availability || 'N/A'
    ]);

    // Add table
    autoTable(doc, {
      startY: 45,
      head: [
        ['Applicant', 'Email', 'Phone', 'Position', 'Company', 'Subjects', 'Teaching Mode', 'Status', 'Applied Date', 'Qualification', 'Experience', 'Availability']
      ],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [102, 126, 234],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Applicant
        1: { cellWidth: 30 }, // Email
        2: { cellWidth: 20 }, // Phone
        3: { cellWidth: 25 }, // Position
        4: { cellWidth: 20 }, // Company
        5: { cellWidth: 25 }, // Subjects
        6: { cellWidth: 18 }, // Teaching Mode
        7: { cellWidth: 18 }, // Status
        8: { cellWidth: 18 }, // Applied Date
        9: { cellWidth: 25 }, // Qualification
        10: { cellWidth: 20 }, // Experience
        11: { cellWidth: 20 }  // Availability
      }
    });

    // Save PDF
    doc.save(`teaching-applications-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleDownloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to opening in new tab
      window.open(url, '_blank');
    }
  };

  const handleDeleteApplication = async (applicationId: string) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      await apiDelete(`/job-applications/${applicationId}`);
      setSuccess('Application deleted successfully');
      loadApplications();
      setDetailsDialog(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete application');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent sx={{ color: 'white' }}>
          <Typography variant="h5" gutterBottom>
            Recruitment Management
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Review and manage teaching job applications. Approve or reject candidates and send email notifications.
          </Typography>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search applicants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="applied">Applied</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="interview_scheduled">Interview Scheduled</MenuItem>
                  <MenuItem value="interviewed">Interviewed</MenuItem>
                  <MenuItem value="offered">Offered</MenuItem>
                  <MenuItem value="hired">Hired</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="withdrawn">Withdrawn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadApplications}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={exportToPDF}
                fullWidth
                disabled={filteredApplications.length === 0}
              >
                Export PDF
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography variant="body2" color="text.secondary" align="right">
                Total: {total} applications
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        {isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 2 }}>
            {loading ? (
              <LinearProgress sx={{ my: 2 }} />
            ) : filteredApplications.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No applications found
              </Typography>
            ) : (
              filteredApplications.map((application) => (
                <Card key={application._id} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar src={application.applicant?.avatar} sx={{ width: 48, height: 48 }}>
                        {application.applicant?.firstName?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {application.applicant?.firstName} {application.applicant?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {application.applicant?.email}
                        </Typography>
                      </Box>
                      <Chip
                        label={application.status.replace('_', ' ')}
                        color={getStatusColor(application.status) as any}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">Position</Typography>
                      <Typography variant="body2">{application.job?.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{application.job?.company}</Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">Subjects</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {application.preferredSubjects?.slice(0, 3).map((subject, idx) => (
                          <Chip key={idx} label={subject} size="small" variant="outlined" />
                        ))}
                        {application.preferredSubjects && application.preferredSubjects.length > 3 && (
                          <Chip label={`+${application.preferredSubjects.length - 3}`} size="small" />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Applied: {formatDate(application.appliedAt)}
                      </Typography>
                      <Box>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(application)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {(application.status === 'applied' || application.status === 'under_review') && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleAction(application, 'approve')}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleAction(application, 'reject')}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Subjects</TableCell>
                  <TableCell>Teaching Mode</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applied Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <LinearProgress sx={{ my: 2 }} />
                    </TableCell>
                  </TableRow>
                ) : filteredApplications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                        No applications found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApplications.map((application) => (
                    <TableRow key={application._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={application.applicant?.avatar} sx={{ width: 32, height: 32 }}>
                            {application.applicant?.firstName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {application.applicant?.firstName} {application.applicant?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {application.applicant?.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {application.job?.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {application.job?.company}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {application.preferredSubjects?.slice(0, 2).map((subject, idx) => (
                            <Chip key={idx} label={subject} size="small" variant="outlined" />
                          ))}
                          {application.preferredSubjects && application.preferredSubjects.length > 2 && (
                            <Chip label={`+${application.preferredSubjects.length - 2}`} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {application.preferredTeachingMode?.replace('_', ' ') || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={application.status.replace('_', ' ')}
                          color={getStatusColor(application.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(application.appliedAt)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(application)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {(application.status === 'applied' || application.status === 'under_review') && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleAction(application, 'approve')}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleAction(application, 'reject')}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Application Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedApplication && (
          <>
            <DialogTitle>
              Application - {selectedApplication.applicant?.firstName} {selectedApplication.applicant?.lastName}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">
                    {selectedApplication.applicant?.firstName} {selectedApplication.applicant?.lastName}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.email}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.phone || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedApplication.status.replace('_', ' ')}
                    color={getStatusColor(selectedApplication.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Position</Typography>
                  <Typography variant="body1">{selectedApplication.job?.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{selectedApplication.job?.company} - {selectedApplication.job?.location}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Preferred Subjects</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedApplication.preferredSubjects?.map((subject, idx) => (
                      <Chip key={idx} label={subject} size="small" />
                    ))}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Teaching Mode</Typography>
                  <Typography variant="body1">{selectedApplication.preferredTeachingMode?.replace('_', ' ') || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Payment Type</Typography>
                  <Typography variant="body1">{selectedApplication.preferredPaymentType?.replace('_', ' ') || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Expected Rate</Typography>
                  <Typography variant="body1">{selectedApplication.expectedRate?.amount ? `${selectedApplication.expectedRate.amount} ${selectedApplication.expectedRate.currency}` : 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Highest Qualification</Typography>
                  <Typography variant="body1">{selectedApplication.highestQualification || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Teaching Experience</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Typography variant="body2">
                      {selectedApplication.teachingExperience || 'No experience provided'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Availability</Typography>
                  <Typography variant="body1">{selectedApplication.availability || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Cover Letter</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Typography variant="body2">
                      {selectedApplication.coverLetter || 'No cover letter provided'}
                    </Typography>
                  </Paper>
                </Grid>
                {(selectedApplication.cvFile || selectedApplication.resume) && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">CV Document</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {selectedApplication.cvFileOriginalName || selectedApplication.resumeOriginalName || 'Document'}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CloudDownload />}
                      onClick={() => handleDownloadFile(
                        selectedApplication.cvFile || selectedApplication.resume,
                        selectedApplication.cvFileOriginalName || selectedApplication.resumeOriginalName || 'document.pdf'
                      )}
                      sx={{ mt: 1 }}
                    >
                      Download CV
                    </Button>
                  </Grid>
                )}
                {selectedApplication.additionalDocuments && selectedApplication.additionalDocuments.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">Additional Documents</Typography>
                    {selectedApplication.additionalDocuments.map((doc, idx) => (
                      <Box key={idx} sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {doc.originalName || 'Document'}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CloudDownload />}
                          onClick={() => handleDownloadFile(doc.url, doc.originalName || 'document.pdf')}
                          sx={{ ml: 1 }}
                        >
                          Download
                        </Button>
                      </Box>
                    ))}
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {(selectedApplication.status === 'applied' || selectedApplication.status === 'under_review') && (
                <>
                  <Button
                    onClick={() => handleAction(selectedApplication, 'reject')}
                    color="error"
                    startIcon={<Cancel />}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleAction(selectedApplication, 'approve')}
                    color="success"
                    startIcon={<CheckCircle />}
                    variant="contained"
                  >
                    Approve
                  </Button>
                </>
              )}
              <Button
                onClick={() => handleDeleteApplication(selectedApplication._id)}
                color="error"
                startIcon={<DeleteOutline />}
              >
                Delete
              </Button>
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, action: null, application: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.action === 'approve' ? 'Approve Application' : 'Reject Application'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {actionDialog.action === 'approve'
              ? `Are you sure you want to approve ${actionDialog.application?.applicant?.firstName} ${actionDialog.application?.applicant?.lastName}?`
              : `Are you sure you want to reject ${actionDialog.application?.applicant?.firstName} ${actionDialog.application?.applicant?.lastName}?`
          }
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionDialog.action === 'approve' ? "Message to Candidate (Optional)" : "Rejection Reason (Optional)"}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
            placeholder={actionDialog.action === 'approve'
              ? "Enter a welcome message or additional information for the candidate..."
              : "Enter feedback for the candidate (optional)..."}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialog({ open: false, action: null, application: null })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            color={actionDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : (actionDialog.action === 'approve' ? <CheckCircle /> : <Cancel />)}
          >
            {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecruitmentManagement;
